import React, { useState } from 'react';
import { Send } from 'lucide-react';

const GOD_COLORS = {
  athena: 'text-amber-400', hermes: 'text-orange-400', apollo: 'text-yellow-400',
  eris: 'text-red-400', themis: 'text-blue-400', calliope: 'text-pink-400',
  hestia: 'text-orange-300', plutus: 'text-green-400', nike: 'text-yellow-400', metis: 'text-indigo-400',
};

const ClashView = ({ clashRounds, onAnotherRound, onCallVerdict, isLoading }) => {
  const [interjection, setInterjection] = useState('');

  const handleInterject = () => {
    if (!interjection.trim()) {
      onAnotherRound(null);
      return;
    }
    onAnotherRound(interjection.trim());
    setInterjection('');
  };

  const allClashes = clashRounds || [];
  const totalRounds = allClashes.length;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="clash-view">
      {/* Clash Rounds */}
      {allClashes.map((round, roundIdx) => (
        <div key={roundIdx} className="space-y-2">
          <div className="flex items-center gap-2 px-1">
            <div className="h-px flex-1 bg-red-500/20" />
            <span className="text-[10px] text-red-400/60 font-semibold uppercase tracking-wider">
              Clash Round {roundIdx + 1}
            </span>
            <div className="h-px flex-1 bg-red-500/20" />
          </div>

          {round.map((clash, idx) => {
            const isDevil = clash.role === 'devil_advocate';
            const textColor = GOD_COLORS[clash.god_id] || 'text-gray-400';

            return (
              <div
                key={`${roundIdx}-${idx}`}
                className={`flex gap-3 p-3 rounded-xl transition-all animate-fade-in ${
                  isDevil
                    ? 'bg-red-900/10 border border-red-500/20'
                    : 'bg-gray-800/20 border border-gray-700/30'
                }`}
                style={{ animationDelay: `${idx * 80}ms` }}
                data-testid={`clash-${clash.god_id}-round-${roundIdx}`}
              >
                <span className="text-xl flex-shrink-0">{clash.god_emoji}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`font-bold text-sm ${textColor}`}>{clash.god_name}</span>
                    {isDevil && (
                      <span className="text-[9px] px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded-full font-semibold">
                        DEVIL'S ADVOCATE
                      </span>
                    )}
                    {clash.challenges && (
                      <span className="text-[10px] text-gray-500 flex items-center gap-1">
                        <span>→</span> challenges <span className={GOD_COLORS[clash.challenges] || 'text-gray-400'}>{clash.challenges}</span>
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-300 leading-relaxed">{clash.content}</p>
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {/* User Interjection Input */}
      {!isLoading && (
        <div className="space-y-3 pt-2">
          <div className="flex gap-2">
            <input
              type="text"
              value={interjection}
              onChange={(e) => setInterjection(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !isLoading && handleInterject()}
              placeholder="Steer the debate (optional)..."
              className="flex-1 bg-gray-800/50 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 focus:outline-none"
              data-testid="clash-interjection-input"
            />
            <button
              onClick={handleInterject}
              disabled={isLoading}
              className="px-4 py-2.5 bg-red-600/80 hover:bg-red-500 text-white rounded-xl transition-all flex items-center gap-1.5 text-sm font-medium disabled:opacity-50"
              data-testid="another-round-btn"
            >
              <Send className="w-4 h-4" />
              <span className="hidden sm:inline">{totalRounds < 2 ? 'Clash Again' : 'Another Round'}</span>
            </button>
          </div>

          <button
            onClick={onCallVerdict}
            disabled={isLoading}
            className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 disabled:opacity-50 text-white font-bold rounded-xl transition-all shadow-lg shadow-green-500/20 hover:shadow-green-500/30 flex items-center justify-center gap-2"
            data-testid="call-verdict-btn"
          >
            Call The Verdict <span className="text-lg">→</span>
          </button>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center gap-3 py-4">
          <div className="w-5 h-5 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
          <span className="text-sm text-gray-400 italic">The gods clash...</span>
        </div>
      )}
    </div>
  );
};

export default ClashView;
